import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import { CheckCircle2, Download, FolderOpen, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useRoute } from "wouter";
import { toast } from "sonner";

export default function ProjectSharePublicPage() {
  const [, params] = useRoute("/share/:token");
  const token = params?.token ?? "";
  const [approveFileId, setApproveFileId] = useState<number | null>(null);
  const [customerComment, setCustomerComment] = useState("");

  const shareQ = trpc.installProjects.getPublicShare.useQuery(
    { token },
    { enabled: token.length >= 16 },
  );

  const meQ = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const approveMut = trpc.installProjects.approveSharedFile.useMutation({
    onSuccess: async () => {
      toast.success("Approved");
      setApproveFileId(null);
      setCustomerComment("");
      await shareQ.refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const loginHref = useMemo(
    () => clarityOneLoginUrlWithRedirect(`/share/${token}`),
    [token],
  );

  if (shareQ.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading shared folder…
      </div>
    );
  }

  if (!shareQ.data) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Link unavailable</CardTitle>
            <CardDescription>This share link is invalid or has been revoked.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const { project, files } = shareQ.data;
  const title =
    "displayTitle" in project && typeof project.displayTitle === "string"
      ? project.displayTitle
      : project.projectName;

  const openApprove = (fileId: number) => {
    if (!meQ.data) {
      window.location.href = loginHref;
      return;
    }
    setCustomerComment("");
    setApproveFileId(fileId);
  };

  const confirmApprove = () => {
    if (approveFileId == null) return;
    approveMut.mutate({
      token,
      fileId: approveFileId,
      customerComment: customerComment.trim() || undefined,
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-center gap-3">
          <FolderOpen className="h-8 w-8 text-[#1a2744]" />
          <div>
            <h1 className="text-xl font-semibold text-[#1a2744]">{title}</h1>
            <p className="text-sm text-muted-foreground">{project.projectRef}</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Shared files</CardTitle>
            <CardDescription>
              View documents immediately. Sign in to Clarity One only when you click Approve.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {files.length === 0 ? (
              <p className="text-sm text-muted-foreground">No files are visible in this folder yet.</p>
            ) : (
              files.map((file) => {
                const approvalStatus =
                  "approvalStatus" in file ? String(file.approvalStatus) : "none";
                const canApprove = file.fileType === "layout_pdf";
                return (
                  <div
                    key={file.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-white p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{file.label}</p>
                      <p className="text-xs text-muted-foreground">{file.fileType}</p>
                      {approvalStatus === "approved" ? (
                        <p className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Approved
                          {"approvedAt" in file && file.approvedAt
                            ? ` · ${new Date(String(file.approvedAt)).toLocaleString()}`
                            : ""}
                        </p>
                      ) : null}
                      {approvalStatus === "updated_since_approval" ? (
                        <p className="mt-1 text-xs text-amber-700">UPDATED SINCE APPROVAL</p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Button type="button" size="sm" variant="outline" asChild>
                        <a href={file.downloadUrl} target="_blank" rel="noreferrer">
                          <Download className="mr-1 h-3.5 w-3.5" />
                          Open
                        </a>
                      </Button>
                      {canApprove ? (
                        <Button
                          type="button"
                          size="sm"
                          disabled={approvalStatus === "approved"}
                          onClick={() => openApprove(file.id)}
                        >
                          {approvalStatus === "approved" ? "Approved" : "Approve"}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={approveFileId != null}
        onOpenChange={(open) => {
          if (!open && !approveMut.isPending) {
            setApproveFileId(null);
            setCustomerComment("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve layout</DialogTitle>
            <DialogDescription>
              Confirm approval for this project layout. You can optionally leave a comment for the
              project team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="customer-approve-comment">Comment (optional)</Label>
            <Textarea
              id="customer-approve-comment"
              value={customerComment}
              onChange={(e) => setCustomerComment(e.target.value.slice(0, 400))}
              placeholder="Add a note for the project team…"
              rows={4}
            />
            <p className="text-xs text-muted-foreground">{customerComment.length}/400</p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={approveMut.isPending}
              onClick={() => {
                setApproveFileId(null);
                setCustomerComment("");
              }}
            >
              Cancel
            </Button>
            <Button type="button" disabled={approveMut.isPending} onClick={confirmApprove}>
              {approveMut.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Confirm approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
